import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router';
import { api, type ItemInventoryData } from '../api';
import { ColorSwatch } from './ItemImage';
import { Badge, Spinner, number, unitMoney } from '../ui';

const LOT_TONE: Record<string, 'slate' | 'green' | 'blue' | 'amber'> = { draft: 'slate', live: 'green', stockroom: 'blue', sold_out: 'amber' };

/** Under an item row: each colour, with New and Used side by side. */
export function ItemRowLots({ itemId }: { itemId: number }) {
  const q = useQuery({ queryKey: ['item-inventory', itemId], queryFn: () => api<ItemInventoryData>(`/api/v1/items/${itemId}/inventory`) });
  if (q.isPending) return <Spinner />;
  if (q.error || !q.data) return <p className="text-sm text-red-700">Could not load this item's stock.</p>;
  const colours = new Map<number, { name: string; rgb: string | null; N?: ItemInventoryData['lots'][number]; U?: ItemInventoryData['lots'][number] }>();
  for (const l of q.data.lots) {
    const c = colours.get(l.colorId) ?? { name: l.colorName, rgb: l.colorRgb };
    c[l.condition as 'N' | 'U'] = l;
    colours.set(l.colorId, c);
  }
  if (colours.size === 0) return <p className="text-sm text-slate-500">No stock of this item yet.</p>;
  const cell = (l?: ItemInventoryData['lots'][number]) =>
    l ? (
      <Link to={`/lots/${l.lotId}`} className="block hover:underline">
        <span className="tabular-nums">{number(l.qty)}</span> <span className="text-slate-500">at</span> {l.price ? unitMoney(l.price) : <span className="text-amber-700">no price</span>}{' '}
        <Badge tone={LOT_TONE[l.status] ?? 'slate'}>{l.status.replace('_', ' ')}</Badge>
      </Link>
    ) : (
      <span className="text-slate-300">—</span>
    );
  return (
    <table className="w-full text-sm">
      <thead>
        <tr className="text-left text-xs text-slate-500">
          <th className="py-1">Colour</th>
          <th>New</th>
          <th>Used</th>
        </tr>
      </thead>
      <tbody>
        {[...colours.entries()].map(([id, c]) => (
          <tr key={id} className="border-t border-slate-100">
            <td className="py-1.5 whitespace-nowrap">
              <ColorSwatch rgb={c.rgb} name={c.name} />
            </td>
            <td>{cell(c.N)}</td>
            <td>{cell(c.U)}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}
